import { FormEvent, useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { apiFetch } from "@/lib/api";
import { useToast } from "@/hooks/useToast";
import { Inhabitant } from "@/types/inhabitants";

const LOCATIONS = ["krasinskiego", "stanczyka", "rydlowka"];

interface Props {
  inhabitant: Inhabitant | null; // null = add mode
  onClose: () => void;
  onSaved: () => void;
}

interface FormState {
  agreementId: string;
  name: string;
  room: string;
  location: string;
  fee: string;
  advanceFee: string;
  noOfInhabitants: string;
  email: string;
  startDate: string;
  endDate: string;
  trashSeparate: boolean;
}

const EMPTY: FormState = {
  agreementId: "",
  name: "",
  room: "",
  location: LOCATIONS[0],
  fee: "",
  advanceFee: "",
  noOfInhabitants: "1",
  email: "",
  startDate: "",
  endDate: "",
  trashSeparate: false,
};

function toFormState(i: Inhabitant): FormState {
  return {
    agreementId: i.agreementId,
    name: i.name,
    room: i.room,
    location: i.location,
    fee: String(i.fee),
    advanceFee: String(i.advanceFee),
    noOfInhabitants: String(i.noOfInhabitants),
    email: i.email,
    startDate: i.startDate,
    endDate: i.endDate,
    trashSeparate: !!i.trashSeparate,
  };
}

export function InhabitantModal({ inhabitant, onClose, onSaved }: Props) {
  const isEdit = inhabitant !== null;
  const [form, setForm] = useState<FormState>(isEdit ? toFormState(inhabitant) : EMPTY);
  const [loading, setLoading] = useState(false);
  const { push } = useToast();

  useEffect(() => {
    setForm(inhabitant ? toFormState(inhabitant) : EMPTY);
  }, [inhabitant]);

  function set(field: keyof FormState, value: string | boolean) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    const body = {
      agreementId: form.agreementId,
      name: form.name,
      room: form.room,
      location: form.location,
      fee: Number(form.fee),
      advance_fee: Number(form.advanceFee),
      no_of_inhabitants: Number(form.noOfInhabitants),
      email: form.email,
      start_date: form.startDate,
      end_date: form.endDate,
      trash_separate: form.trashSeparate,
    };
    try {
      if (isEdit) {
        await apiFetch(`/api/inhabitants/${inhabitant.agreementId}`, {
          method: "PUT",
          body: JSON.stringify(body),
        });
        push({ message: `Zapisano zmiany: ${form.name}`, tone: "success" });
      } else {
        await apiFetch("/api/inhabitants", {
          method: "POST",
          body: JSON.stringify(body),
        });
        push({ message: `Dodano mieszkańca: ${form.name}`, tone: "success" });
      }
      onSaved();
      onClose();
    } catch (err) {
      push({ message: (err as Error).message, tone: "danger" });
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? `Edytuj: ${inhabitant.name}` : "Dodaj mieszkańca"}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={loading}>Anuluj</Button>
          <Button onClick={submit} loading={loading}
                  disabled={!form.name || !form.agreementId || !form.startDate || !form.endDate}>
            {isEdit ? "Zapisz zmiany" : "Dodaj"}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input id="f-id" label="ID umowy" value={form.agreementId}
               onChange={(e) => set("agreementId", e.target.value)}
               disabled={isEdit}
               placeholder="np. 101" />
        <Input id="f-name" label="Imię i nazwisko" value={form.name}
               onChange={(e) => set("name", e.target.value)}
               autoFocus={!isEdit}
               placeholder="Jan Kowalski" />
        <Input id="f-room" label="Pokój / opis lokalu" value={form.room}
               onChange={(e) => set("room", e.target.value)}
               placeholder="np. pokój 2" />
        <Select id="f-loc" label="Lokal" value={form.location}
                onChange={(e) => set("location", e.target.value)}>
          {LOCATIONS.map((l) => <option key={l} value={l}>{l}</option>)}
        </Select>
        <Input id="f-fee" label="Czynsz (PLN)" inputMode="numeric" value={form.fee}
               onChange={(e) => set("fee", e.target.value)} placeholder="0" />
        <Input id="f-adv" label="Zaliczka (PLN)" inputMode="numeric" value={form.advanceFee}
               onChange={(e) => set("advanceFee", e.target.value)} placeholder="0" />
        <Input id="f-pers" label="Liczba osób" inputMode="numeric" value={form.noOfInhabitants}
               onChange={(e) => set("noOfInhabitants", e.target.value)} placeholder="1" />
        <Input id="f-email" label="E-mail" type="email" value={form.email}
               onChange={(e) => set("email", e.target.value)} placeholder="jan@example.com" />
        <Input id="f-start" label="Data rozpoczęcia" type="date" value={form.startDate}
               onChange={(e) => set("startDate", e.target.value)} />
        <Input id="f-end" label="Data zakończenia" type="date" value={form.endDate}
               onChange={(e) => set("endDate", e.target.value)} />
        <div className="sm:col-span-2 flex items-center gap-2.5 pt-1">
          <input type="checkbox" id="f-trash" checked={form.trashSeparate}
                 onChange={(e) => set("trashSeparate", e.target.checked)}
                 className="h-4 w-4 rounded border-border accent-accent cursor-pointer" />
          <label htmlFor="f-trash" className="text-sm cursor-pointer select-none">
            Śmieci rozliczane osobno
          </label>
        </div>
      </form>
    </Modal>
  );
}
